<template>
  <div
    aria-valuemax="100"
    aria-valuemin="0"
    :aria-valuenow="value"
    class="s-progress"
    :class="[
      's-progress--' + type,
      status ? 'is-' + status : '',
      {
        's-progress--without-text': !showText,
        's-progress--text-inside': textInside,
        's-progress--indeterminate': indeterminate
      }
    ]"
    role="progressbar">
    <div
      v-if="type === 'line'"
      class="s-progress-bar">
      <div
        class="s-progress-bar__outer"
        :style="{height: strokeWidth + 'px', backgroundColor:defineBackColor}">
        <div
          class="s-progress-bar__inner"
          :style="barStyle">
          <div
            v-if="showText && textInside"
            class="s-progress-bar__innerText"
            :style="{color:textColor}">
            {{ content }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="s-progress-circle"
      :style="{height: width + 'px', width: width + 'px'}">
      <svg viewBox="0 0 100 100">
        <path
          class="s-progress-circle__track"
          :d="trackPath"
          fill="none"
          :stroke="defineBackColor"
          :stroke-width="relativeStrokeWidth"
          :style="trailPathStyle"/>
        <path
          class="s-progress-circle__path"
          :d="trackPath"
          fill="none"
          :stroke="stroke"
          :stroke-linecap="strokeLinecap"
          :stroke-width="value ? relativeStrokeWidth : 0"
          :style="circlePathStyle"/>
      </svg>
    </div>
    <div
      v-if="showText && !textInside"
      class="s-progress__text"
      :style="{fontSize: progressTextSize + 'px', color:textColor}">
      <template v-if="!status">
        {{ content }}
      </template>
      <sIcon
        v-else
        :type="iconType"/>
    </div>
  </div>
</template>
<script>
import sIcon from './icon'

export default {
  name: 'sProgress',
  methods: {
    getCurrentColor(percentage) {
      if (typeof this.color === 'function') {
        return this.color(percentage)
      } else if (typeof this.color === 'string') {
        return this.color
      } else {
        return this.getLevelColor(percentage)
      }
    },
    getLevelColor(percentage) {
      const colorArray = this.getColorArray().sort((a, b) => a.percentage - b.percentage)

      for (let i = 0; i < colorArray.length; i++) {
        if (colorArray[i].percentage > percentage) {
          return colorArray[i].color
        }
      }
      return colorArray[colorArray.length - 1].color
    },
    getColorArray() {
      const color = this.color
      const span = 100 / color.length
      return color.map((seriesColor, index) => {
        if (typeof seriesColor === 'string') {
          return {
            color: seriesColor,
            percentage: (index + 1) * span
          }
        }
        return seriesColor
      })
    }
  },
  computed: {
    barStyle() {
      const style = {}
      const value = this.indeterminate ? 50 : this.value
      style.width = value + '%'
      style.backgroundColor = this.getCurrentColor(value)
      return style
    },
    relativeStrokeWidth() {
      return (this.strokeWidth / this.width * 100).toFixed(1)
    },
    radius() {
      if (this.type === 'circle' || this.type === 'dashboard') {
        return parseInt(50 - parseFloat(this.relativeStrokeWidth) / 2, 10)
      } else {
        return 0
      }
    },
    trackPath() {
      const radius = this.radius
      const isDashboard = this.type === 'dashboard'
      return `
          M 50 50
          m 0 ${isDashboard ? '' : '-'}${radius}
          a ${radius} ${radius} 0 1 1 0 ${isDashboard ? '-' : ''}${radius * 2}
          a ${radius} ${radius} 0 1 1 0 ${isDashboard ? '' : '-'}${radius * 2}
          `
    },
    perimeter() {
      return 2 * Math.PI * this.radius
    },
    rate() {
      return this.type === 'dashboard' ? 0.75 : 1
    },
    strokeDashoffset() {
      const offset = -1 * this.perimeter * (1 - this.rate) / 2
      return `${offset}px`
    },
    trailPathStyle() {
      return {
        strokeDasharray: `${(this.perimeter * this.rate)}px, ${this.perimeter}px`,
        strokeDashoffset: this.strokeDashoffset
      }
    },
    circlePathStyle() {
      return {
        strokeDasharray: `${this.perimeter * this.rate * (this.value / 100) }px, ${this.perimeter}px`,
        strokeDashoffset: this.strokeDashoffset,
        transition: 'stroke-dasharray 0.6s ease 0s, stroke 0.6s ease'
      }
    },
    stroke() {
      let ret
      if (this.color) {
        ret = this.getCurrentColor(this.value)
      } else {
        switch (this.status) {
        case 'success':
          ret = 'var(--color-success)'
          break
        case 'exception':
          ret = 'var(--color-error)'
          break
        case 'warning':
          ret = 'var(--color-warning)'
          break
        default:
          ret = 'var(--color-info)'
        }
      }
      return ret
    },
    iconType() {
      if (this.status === 'warning') {
        return 'alert-outline'
      }
      if (this.type === 'line') {
        return this.status === 'success' ? 'accept' : 'cancel'
      } else {
        return this.status === 'success' ? 'okay-arrow' : 'crossed'
      }
    },
    progressTextSize() {
      return this.type === 'line'
        ? 12 + this.strokeWidth * 0.4
        : this.width * 0.111111 + 2
    },
    content() {
      if (typeof this.format === 'function') {
        return this.format(this.value) || ''
      } else {
        return `${this.value}%`
      }
    }
  },
  components: {
    sIcon
  },
  props: {
    /**
     * тип индикатора выполнения
     */
    type: {
      type: String,
      default: 'line',
      validator: val => ['line', 'circle', 'dashboard'].indexOf(val) > -1
    },
    /**
     * процент, обязательный
     */
    value: {
      type: Number,
      default: 0,
      validator: val => val >= 0 && val <= 100
    },
    /**
     * текущий статус индикатора выполнения
     */
    status: {
      type: String,
      validator: val => ['success', 'exception', 'warning'].indexOf(val) > -1
    },
    /**
     * 	ширина индикатора выполнения
     */
    strokeWidth: {
      type: Number,
      default: 4
    },
    /**
     * Форма
     */
    strokeLinecap: {
      type: String,
      default: 'round',
      validator: val => ['butt', 'round', 'square'].indexOf(val) > -1
    },
    /**
     * помещать ли процент в индикатор выполнения, работает только в том случае, если тип «линия»
     */
    textInside: {
      type: Boolean,
      default: false
    },
    /**
     * ширина холста кругового индикатора выполнения
     */
    width: {
      type: Number,
      default: 126
    },
    /**
     * показывать ли процент
     */
    showText: {
      type: Boolean,
      default: false
    },
    /**
     * цвет фона индикатора выполнения. Переопределяет реквизит статуса
     */
    color: {
      type: [String, Array, Function],
      default: ''
    },
    /**
     * цвет фона индикатора выполнения (шестнадцатеричный формат)
     */
    defineBackColor: {
      type: [String, Array, Function],
      default: 'var(--v-secondary__green_chosen-base)'
    },
    /**
     * цвет текста индикатора выполнения (шестнадцатеричный формат)
     */
    textColor: {
      type: [String, Array, Function],
      default: 'var(--text-color)'
    },
    /**
     * пользовательский формат текста
     */
    format: Function,
    indeterminate: {
      type: Boolean,
      default: false
    },
  },
}
</script>
